import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronLeft, ChevronRight, ArrowRight, Search, X } from "lucide-react";
import { EVENTI_FULL } from "@/data/eventiData";

const TIPI = ["Tutte", "Convegno", "Evento", "Seminario", "Webinar", "Workshop"];

const TIPO_COLORS = {
  "Tutte":     { chip: "border-[var(--confcom-border)] text-[var(--confcom-text-secondary)] hover:border-[var(--confcom-blue)] hover:text-[var(--confcom-blue)]", active: "bg-[var(--confcom-blue)] text-white border-[var(--confcom-blue)]" },
  "Convegno":  { chip: "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100",  active: "bg-indigo-600 text-white border-indigo-600" },
  "Evento":    { chip: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",          active: "bg-blue-600 text-white border-blue-600" },
  "Seminario": { chip: "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100",          active: "bg-teal-600 text-white border-teal-600" },
  "Webinar":   { chip: "bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100",          active: "bg-cyan-600 text-white border-cyan-600" },
  "Workshop":  { chip: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100", active: "bg-purple-600 text-white border-purple-600" },
};

const PER_PAGE = 8;

export default function EventiPage() {
  const [q, setQ] = useState("");
  const [tipoAttivo, setTipoAttivo] = useState("Tutte");
  const [page, setPage] = useState(1);
  const filterRef = useRef(null);

  const scrollToResults = () => {
    if (filterRef.current) {
      window.scrollTo({ top: filterRef.current.offsetTop - (window.innerWidth >= 1024 ? 168 : 104), behavior: "smooth" });
    }
  };

  const filtered = useMemo(() => {
    let result = EVENTI_FULL;
    if (tipoAttivo !== "Tutte") result = result.filter((e) => e.tipo === tipoAttivo);
    if (q.trim()) {
      const lq = q.toLowerCase();
      result = result.filter((e) => e.titolo.toLowerCase().includes(lq) || (e.tipo || "").toLowerCase().includes(lq));
    }
    return result;
  }, [q, tipoAttivo]);

  const isFiltering = q.trim() !== "" || tipoAttivo !== "Tutte";
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const start = (page - 1) * PER_PAGE;
  const eventiPagina = filtered.slice(start, start + PER_PAGE);

  const handleFilter = (tipo) => {
    setTipoAttivo(tipo);
    setPage(1);
    scrollToResults();
  };

  return (
    <main id="main-content" data-testid="eventi-page">

      {/* Hero */}
      <section className="relative overflow-hidden" aria-labelledby="eventi-title">
        <img src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1920&q=60&auto=format" alt="Evento conferenza" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">In agenda</span>
            <h1 id="eventi-title" className="text-4xl md:text-5xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">Appuntamenti</h1>
            <p className="text-lg text-white/80 mt-4 max-w-3xl">
              Convegni, workshop, webinar e seminari organizzati da Confcommercio Roma per le imprese e i professionisti.
            </p>
          </div>
          <div className="mt-10 max-w-xl">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" aria-hidden="true" />
              <input
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Cerca appuntamenti…"
                data-testid="eventi-search"
                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/15 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-white/60 focus:bg-white/20 transition-all text-sm"
              />
              {q && (
                <button onClick={() => { setQ(""); setPage(1); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors" aria-label="Cancella ricerca">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Barra filtri sticky – full width */}
      <section ref={filterRef} className="sticky top-[104px] lg:top-[168px] z-20 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 py-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--confcom-text-muted)] shrink-0">Tipo</span>
            {TIPI.map((tipo) => {
              const isActive = tipoAttivo === tipo;
              const cc = TIPO_COLORS[tipo] || TIPO_COLORS["Tutte"];
              const count = tipo === "Tutte" ? EVENTI_FULL.length : EVENTI_FULL.filter((e) => e.tipo === tipo).length;
              return (
                <button
                  key={tipo}
                  onClick={() => handleFilter(tipo)}
                  data-testid={`filter-tipo-${tipo.toLowerCase()}`}
                  aria-pressed={isActive}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all ${isActive ? cc.active : cc.chip}`}
                >
                  {tipo}
                  <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full leading-none ${isActive ? "bg-white/25" : "bg-white/60"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lista eventi */}
      <section className="py-12 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">          {/* Risultati / reset */}
          {isFiltering && (
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[var(--confcom-text-secondary)]">
                <span className="font-bold text-[var(--confcom-text-primary)]">{filtered.length}</span> appuntamenti trovati
              </p>
              <button
                onClick={() => { setQ(""); setTipoAttivo("Tutte"); setPage(1); }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--confcom-blue)] border border-[var(--confcom-blue)]/30 px-3 py-1.5 rounded-full hover:bg-[var(--confcom-bg-blue-light)] transition-colors"
                data-testid="reset-filtri"
              >
                <X size={12} /> Rimuovi filtri
              </button>
            </div>
          )}

          {/* Griglia */}
          {eventiPagina.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[var(--confcom-text-muted)] mb-3">Nessun evento trovato con i filtri selezionati.</p>
              <button onClick={() => { setQ(""); setTipoAttivo("Tutte"); setPage(1); }} className="text-sm text-[var(--confcom-blue)] hover:underline">Reimposta i filtri</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6" data-testid="events-list">
              {eventiPagina.map((e) => (
                <Link key={e.id} to={`/eventi/${e.slug}`} className="group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:shadow-lg transition-all" data-testid={`event-item-${e.id}`}>
                  <div className="h-44 overflow-hidden relative">
                    <img src={e.image} alt={e.titolo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute top-4 left-4 w-16 h-16 bg-[var(--confcom-blue)] rounded-xl flex flex-col items-center justify-center text-white shadow-lg">
                      <span className="text-2xl font-bold font-['Encode_Sans_Semi_Condensed'] leading-none">{e.giorno}</span>
                      <span className="text-xs uppercase font-medium">{e.mese}</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${(TIPO_COLORS[e.tipo] || TIPO_COLORS["Tutte"]).chip}`}>{e.tipo}</span>
                      <span className="text-xs text-[var(--confcom-text-muted)]">{e.data}</span>
                    </div>
                    <h2 className="text-base font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors mb-2">{e.titolo}</h2>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--confcom-blue)]">Scopri di più <ArrowRight size={12} /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Paginazione */}
          {totalPages > 1 && (
            <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Paginazione appuntamenti" data-testid="eventi-pagination">
              <button onClick={() => { setPage(Math.max(1, page - 1)); scrollToResults(); }} disabled={page === 1} className="w-10 h-10 rounded-lg border border-[var(--confcom-border)] bg-white flex items-center justify-center text-[var(--confcom-text-muted)] hover:text-[var(--confcom-blue)] hover:border-[var(--confcom-blue)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Pagina precedente">
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => { setPage(p); scrollToResults(); }} className={`w-10 h-10 rounded-lg text-sm font-semibold transition-colors ${p === page ? "bg-[var(--confcom-blue)] text-white" : "border border-[var(--confcom-border)] bg-white text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] hover:border-[var(--confcom-blue)]"}`} aria-label={`Pagina ${p}`} aria-current={p === page ? "page" : undefined}>{p}</button>
              ))}
              <button onClick={() => { setPage(Math.min(totalPages, page + 1)); scrollToResults(); }} disabled={page === totalPages} className="w-10 h-10 rounded-lg border border-[var(--confcom-border)] bg-white flex items-center justify-center text-[var(--confcom-text-muted)] hover:text-[var(--confcom-blue)] hover:border-[var(--confcom-blue)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Pagina successiva">
                <ChevronRight size={18} />
              </button>
            </nav>
          )}
          <p className="text-center text-xs text-[var(--confcom-text-muted)] mt-4">
            Pagina {page} di {Math.max(1, totalPages)} — {filtered.length} appuntamenti
          </p>

        </div>
      </section>
    </main>
  );
}
